/* eslint-disable */
// Admin-only "Edit Event" dialog (iter-90, formerly inline in EventDetail.jsx).
// Extracted from EventDetail.jsx in iter-126 (slice 3) to bring the parent
// page below 3900 LOC.
//
// Props
// -----
//   open                          boolean — dialog open state
//   onOpenChange                  Radix onOpenChange handler (also used to close)
//   editForm                      object — the in-progress form state owned by parent
//   setEditForm                   setter for editForm
//   reminderCatalogue             list of { code, label, category } reminder offsets
//   eventId                       string — for the "send sample reminder" endpoint
//   sendingTestReminder           boolean — disables the test-reminder button
//   setSendingTestReminder        setter for sendingTestReminder
//   saving                        boolean — disables the Save button
//   onSave                        async () => void — persist changes
import React from "react";
import { Bell } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { toast } from "sonner";
import { apiClient } from "../../App";
import InvitedPlayersPicker from "../../components/InvitedPlayersPicker";

export const EditEventDialog = ({
  open,
  onOpenChange,
  editForm,
  setEditForm,
  reminderCatalogue,
  eventId,
  sendingTestReminder,
  setSendingTestReminder,
  saving,
  onSave,
}) => {
  if (!editForm) return null;

  const handleSendTestReminder = async () => {
    setSendingTestReminder(true);
    try {
      const res = await apiClient.post(`/events/${eventId}/test-reminder`);
      const target = res.data?.sent_to || "your inbox";
      const pushMsg = res.data?.push_sent
        ? " + push"
        : res.data?.push_subscribed === false
          ? " (push skipped — enable web push on this device first)"
          : "";
      toast.success(`Sample reminder sent to ${target}${pushMsg}`);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Failed to send test reminder";
      const isSenderError = err?.response?.status === 412;
      toast.error(msg, { duration: isSenderError ? 12000 : 5000 });
    } finally {
      setSendingTestReminder(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="edit-event-dialog">
        <DialogHeader>
          <DialogTitle>Edit Event</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label htmlFor="edit-title">Event Title</Label>
            <Input
              id="edit-title"
              value={editForm.title}
              onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
              data-testid="edit-event-title"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="edit-event-type">Event Type</Label>
              <Select
                value={editForm.event_type}
                onValueChange={(v) => setEditForm({ ...editForm, event_type: v })}
              >
                <SelectTrigger id="edit-event-type" data-testid="edit-event-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="rehearsal">Rehearsal</SelectItem>
                  <SelectItem
                    value="rehearsal_sectional"
                    data-testid="edit-option-sectional"
                  >
                    Rehearsal — Sectional
                  </SelectItem>
                  <SelectItem value="concert">Concert</SelectItem>
                  <SelectItem value="contest" data-testid="edit-option-contest">Contest</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="edit-status">Status</Label>
              <Select
                value={editForm.status}
                onValueChange={(v) => setEditForm({ ...editForm, status: v })}
              >
                <SelectTrigger id="edit-status" data-testid="edit-event-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="provisional">Provisional</SelectItem>
                  <SelectItem value="confirmed">Confirmed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* iter-159: sectional-only player picker */}
          {editForm.event_type === "rehearsal_sectional" && (
            <InvitedPlayersPicker
              value={editForm.invited_player_ids || []}
              onChange={(ids) =>
                setEditForm({ ...editForm, invited_player_ids: ids })
              }
              testidPrefix="edit-sectional"
            />
          )}

          <div>
            <Label htmlFor="edit-location">Location</Label>
            <Input
              id="edit-location"
              value={editForm.location}
              onChange={(e) => setEditForm({ ...editForm, location: e.target.value })}
              data-testid="edit-event-location"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="edit-arrival">Arrival Time</Label>
                <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editForm.arrival_time_tbc}
                    onChange={(e) => setEditForm({ ...editForm, arrival_time_tbc: e.target.checked })}
                    data-testid="edit-arrival-tbc"
                  />
                  TBC
                </label>
              </div>
              <Input
                id="edit-arrival"
                type="datetime-local"
                value={editForm.arrival_time}
                onChange={(e) => setEditForm({ ...editForm, arrival_time: e.target.value })}
                data-testid="edit-event-arrival"
              />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="edit-start">Start Time</Label>
                <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editForm.start_time_tbc}
                    onChange={(e) => setEditForm({ ...editForm, start_time_tbc: e.target.checked })}
                    data-testid="edit-start-tbc"
                  />
                  TBC
                </label>
              </div>
              <Input
                id="edit-start"
                type="datetime-local"
                value={editForm.start_time}
                onChange={(e) => setEditForm({ ...editForm, start_time: e.target.value })}
                data-testid="edit-event-start"
              />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between">
              <Label htmlFor="edit-end">End Time</Label>
              <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer">
                <input
                  type="checkbox"
                  checked={editForm.end_time_tbc}
                  onChange={(e) => setEditForm({ ...editForm, end_time_tbc: e.target.checked })}
                  data-testid="edit-end-tbc"
                />
                TBC
              </label>
            </div>
            <Input
              id="edit-end"
              type="datetime-local"
              value={editForm.end_time}
              onChange={(e) => setEditForm({ ...editForm, end_time: e.target.value })}
              data-testid="edit-event-end"
            />
          </div>

          <div>
            <Label htmlFor="edit-description">Details</Label>
            <Textarea
              id="edit-description"
              value={editForm.description}
              onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
              rows={3}
              data-testid="edit-event-description"
            />
          </div>

          {/* iter-134j: `is_private` toggle mirrors what Events.jsx offers on
              create. Private events are hidden from the public concert embed
              widget — admins can flip a legacy event private after the fact. */}
          <div className="flex items-center justify-between pt-3 border-t border-white/[0.06]">
            <div className="space-y-0.5">
              <Label className="text-sm">Private event</Label>
              <p className="text-xs text-muted-foreground">
                Hide from the public concerts embed on your band website. Members inside the app can still see it.
              </p>
            </div>
            <Switch
              checked={!!editForm.is_private}
              onCheckedChange={(checked) =>
                setEditForm({ ...editForm, is_private: checked })
              }
              data-testid="edit-event-private-switch"
            />
          </div>

          {/* iter-90: Event reminders are editable from this dialog so admins
              can switch them on after the event was first created without. */}
          <div className="space-y-3 pt-3 border-t border-white/[0.06]">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label className="flex items-center gap-2 text-sm">
                  <Bell className="w-4 h-4 text-primary" />
                  Email Reminders
                </Label>
                <p className="text-xs text-muted-foreground">
                  Send reminders to players who responded Yes or Maybe
                </p>
              </div>
              <Switch
                checked={!!editForm.reminder_enabled}
                onCheckedChange={(checked) =>
                  setEditForm({ ...editForm, reminder_enabled: checked })
                }
                data-testid="edit-reminder-enabled-switch"
              />
            </div>

            {editForm.reminder_enabled && (
              <div className="space-y-2 pl-6 animate-fade-in">
                <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  When to send reminders (pick as many as you like)
                </Label>
                {/* iter-107n: same 3-category multi-select grid as
                    the Events.jsx dialog. Empty `reminder_offsets`
                    for legacy events is back-filled from
                    `reminder_hours` in handleEdit so the existing
                    setting stays visible. */}
                {(() => {
                  if (!reminderCatalogue.length) {
                    return <p className="text-xs text-muted-foreground">Loading options…</p>;
                  }
                  const grouped = {
                    far_ahead: { label: "Far ahead", items: [] },
                    close: { label: "Close to the event", items: [] },
                    smart: { label: "Smart timing", items: [] },
                  };
                  for (const opt of reminderCatalogue) {
                    (grouped[opt.category]?.items || []).push(opt);
                  }
                  const selected = new Set(editForm.reminder_offsets || []);
                  const toggle = (code) => {
                    const next = new Set(selected);
                    if (next.has(code)) next.delete(code);
                    else next.add(code);
                    setEditForm({ ...editForm, reminder_offsets: [...next] });
                  };
                  return (
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      {Object.entries(grouped).map(([key, group]) => (
                        <div key={key} className="space-y-1.5">
                          <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/80">
                            {group.label}
                          </p>
                          {group.items.map((opt) => (
                            <label
                              key={opt.code}
                              className="flex items-center gap-2 text-xs cursor-pointer rounded p-1.5 hover:bg-white/5"
                              data-testid={`edit-reminder-offset-${opt.code}`}
                            >
                              <input
                                type="checkbox"
                                checked={selected.has(opt.code)}
                                onChange={() => toggle(opt.code)}
                              />
                              <span>{opt.label}</span>
                            </label>
                          ))}
                        </div>
                      ))}
                    </div>
                  );
                })()}
                <p className="text-xs text-muted-foreground">
                  Non-responders will automatically receive a reminder 7 days before.
                </p>
                <div className="pt-3 mt-3 border-t border-white/[0.06]">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={sendingTestReminder}
                    onClick={handleSendTestReminder}
                    data-testid="edit-send-test-reminder-btn"
                    className="text-xs"
                  >
                    <Bell className="w-3.5 h-3.5 mr-1.5" />
                    {sendingTestReminder ? "Sending…" : "Send sample reminder to me"}
                  </Button>
                  <p className="text-[11px] text-muted-foreground mt-1.5">
                    Fires a one-off preview to your inbox using this event&apos;s current details + programme. Members are not emailed.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={onSave}
            disabled={saving || !editForm.title}
            data-testid="save-edit-event-btn"
          >
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EditEventDialog;
